import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiSave, FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";

const AdminRental = () => {
  const [rental, setRental] = useState({
    heading: "",
    subHeading: "",
    rentalRules: [],
  });
  const [initialRental, setInitialRental] = useState({
    heading: "",
    subHeading: "",
    rentalRules: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch rental content on mount
  useEffect(() => {
    const fetchRental = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/rental/get-rental-data`
        );
        if (data?.rental) {
          setRental(data.rental);
          setInitialRental(data.rental);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRental();
  }, []);

  // Handle save
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/rental/create-rental-data`,
        {
          heading: rental.heading,
          subHeading: rental.subHeading,
          rentalRules: rental.rentalRules,
        }
      );
      setMessage(data.message || "Rental data saved successfully!");
      setTimeout(() => setMessage(null), 5000);
      setInitialRental(rental);
      setEditing(false);
    } catch (err) {
      setMessage(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddRule = () => {
    setRental({
      ...rental,
      rentalRules: [...rental.rentalRules, ""],
    });
  };

  const handleRuleChange = (index, value) => {
    const updatedRules = [...rental.rentalRules];
    updatedRules[index] = value;
    setRental({ ...rental, rentalRules: updatedRules });
  };

  const handleRemoveRule = (index) => {
    const updatedRules = rental.rentalRules.filter((_, i) => i !== index);
    setRental({ ...rental, rentalRules: updatedRules });
  };

  // Detect unsaved changes
  const hasChanges =
    rental.heading !== initialRental.heading ||
    rental.subHeading !== initialRental.subHeading ||
    JSON.stringify(rental.rentalRules) !==
      JSON.stringify(initialRental.rentalRules);

  return (
    <div className="min-h-screen bg-transparent p-6 w-full">
      <h1 className="text-3xl font-heading font-bold mb-8 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
        Manage Rental Content
      </h1>

      {loading ? (
        <div className="bg-white shadow-xl rounded-2xl p-6 space-y-6 animate-pulse">
          <div className="h-4 w-32 bg-gray-300 rounded mb-2"></div>
          <div className="h-10 w-full bg-gray-200 rounded"></div>
        </div>
      ) : (
        <form
          onSubmit={handleSave}
          className="bg-white rounded-2xl space-y-6 max-w-full"
        >
          {/* Heading */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Heading
            </label>
            <input
              type="text"
              value={rental.heading}
              onChange={(e) =>
                setRental({ ...rental, heading: e.target.value })
              }
              className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
              placeholder="Enter rental heading"
              required
              disabled={!editing}
            />
          </div>

          {/* Subheading */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sub Heading
            </label>
            <textarea
              value={rental.subHeading}
              onChange={(e) =>
                setRental({ ...rental, subHeading: e.target.value })
              }
              className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
              placeholder="Enter rental subheading"
              rows={4}
              required
              disabled={!editing}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Rental Rules
              </label>
              {editing && (
                <button
                  type="button"
                  onClick={handleAddRule}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  <FiPlus size={16} />
                  Add Rule
                </button>
              )}
            </div>

            <div className="space-y-3">
              {rental.rentalRules.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No rental rules added yet.
                </p>
              ) : (
                rental.rentalRules.map((rule, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 mt-2 min-w-[24px]">
                      {index + 1}.
                    </span>
                    <textarea
                      value={rule}
                      onChange={(e) => handleRuleChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                      placeholder={`Enter rule ${index + 1}`}
                      rows={2}
                      required
                      disabled={!editing}
                    />
                    {editing && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRule(index)}
                        className="mt-2 text-red-500 hover:text-red-700 transition"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Buttons */}
          <div
            className={`w-full flex items-center ${
              message ? "justify-between" : "justify-end"
            } gap-4`}
          >
            {message && (
              <p className="text-sm text-green-600 font-medium">{message}</p>
            )}
            {!editing ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="w-52 px-6 py-2 rounded-xl shadow-md font-medium 
                 text-gray-800 bg-gradient-to-r from-gray-100 to-gray-300
                 hover:from-gray-200 hover:to-gray-400 
                 active:scale-95 transition-all duration-200 ease-in-out
                 flex items-center justify-center gap-2"
              >
                <FiEdit className="text-lg text-gray-600" />
                Edit Details
              </button>
            ) : (
              <button
                type="submit"
                disabled={saving || !hasChanges}
                className="flex items-center justify-center w-52 gap-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white px-6 py-2 rounded-lg shadow-lg hover:opacity-90 transition disabled:opacity-50"
              >
                {saving ? (
                  <div className="spinner w-full"></div>
                ) : (
                  <>
                    <FiSave size={18} />
                    Save Changes
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminRental;
